import { type Location } from '@koala/sdk';
import { CrossStreet } from '../crossStreet';
import MenuCtaManager from '../ctas/menuCta';
import { LocationCta } from '../ctas/primaryCta';
import SecondaryCtaManager from '../ctas/secondaryCta';
import { CardInfoRow, getLocationAvailability, LocationConveyance } from './components';
import {
  StyledCardInfo,
  StyledCardLinks,
  StyledDistance,
  StyledLocationCard,
  StyledLocationCardFlex,
  StyledNameRow,
  StyledPinBadge,
  StyledSelectedIndicator,
  StyledVisuallyHidden,
} from './styles';
import { FavoriteLocationActions } from '@/components/locations/favorite-location-actions';
import { CheckMarkCircle } from '@/components/uielements/icon/check-mark-circle';
import { ClockIcon } from '@/components/uielements/icon/clock';
import { MapIcon } from '@/components/uielements/icon/map';
import { Stack } from '@/components/uielements/stack/stack';
import { CSS_CLASSES } from '@/constants/cssClassNames';
import { LOCATION_ATTRIBUTE_POSITIONS } from '@/constants/locations';
import { PhoneIcon } from '@/features/menu-availability/icons/phone';
import { MapPinIcon } from '@/components/uielements/icon/map-pin';
import { usePrefetchMenu } from '@/features/menu/service';
import { deriveTodaysHours, googleMapsUrl } from '@/utils/locations';
import { phoneNumberFormatter } from '@/utils/auth';

interface Props {
  activeLocationId?: number;
  location: Location;
  setActiveLocation?: (id: number) => void;
  menuCta?: string;
  favorites?: boolean;
  editFavoriteLocation?: boolean;
}

export const LocationCard = ({
  activeLocationId,
  menuCta,
  location,
  setActiveLocation,
  favorites,
  editFavoriteLocation,
}: Props) => {
  const prefetchMenu = usePrefetchMenu();

  if (!location) {
    return null;
  }

  const isActive = activeLocationId === location.id;

  const deriveLocationInfo = (location: Location) => {
    const { cached_data } = location;
    const todaysHours = deriveTodaysHours(location, cached_data ? cached_data.utc_offset : 0);
    const phone = phoneNumberFormatter(cached_data?.phone_number);
    const availability = getLocationAvailability(location, todaysHours);

    // Favorites cards are never selected, so their names keep the full selected weight.
    return (
      <StyledCardInfo $isActive={isActive || favorites}>
        <StyledNameRow>
          <StyledPinBadge aria-hidden="true">
            <MapPinIcon />
          </StyledPinBadge>
          <div role="region" aria-label="Location name">
            <h3>{cached_data?.label}</h3>
          </div>
          {isActive && (
            <StyledSelectedIndicator data-testid="location-card-selected-indicator">
              <span aria-hidden="true">
                <CheckMarkCircle title="Selected" width="20" height="20" />
              </span>
              <StyledVisuallyHidden>Selected location</StyledVisuallyHidden>
            </StyledSelectedIndicator>
          )}
        </StyledNameRow>

        <CardInfoRow icon={<MapIcon />} testId="location-card-address-row">
          <a
            data-testid="locations-card-address-container"
            href={googleMapsUrl(cached_data)}
            target={cached_data ? '_blank' : '_self'}
            rel="noreferrer"
          >
            {location.street_address}&nbsp;
            <br />
            <CrossStreet crossStreetDetail={location.cross_street} />
            {location.city}, {cached_data?.state} {location.zip_code}
          </a>
        </CardInfoRow>

        {phone && (
          <CardInfoRow icon={<PhoneIcon />} testId="location-card-phone-row">
            <span data-testid="location-phone">{phone}</span>
          </CardInfoRow>
        )}

        {availability && (
          <CardInfoRow icon={<ClockIcon />} testId="location-card-availability-row">
            {availability}
          </CardInfoRow>
        )}

        <LocationConveyance location={location} />

        {/* Show distance on mobile list view, since there's no map */}
        {location.distance_miles && location.distance_miles > 0 && (
          <StyledDistance>
            {Math.round(location.distance_miles * 100) / 100} miles away
          </StyledDistance>
        )}
      </StyledCardInfo>
    );
  };

  return (
    <StyledLocationCard
      // location card is not clickable on mobile map display
      className={`${CSS_CLASSES.LOCATION_CARD.CONTAINER}${isActive ? ' active' : ''}`}
      aria-current={isActive ? 'true' : undefined}
      /** @TODO it's not accessible to have an `onClick` on a div. Fix this. */
      onClick={() => {
        // Warm the menu cache on select so the fetch overlaps the route
        // transition to the store page instead of starting after it. (TEAM-13663)
        prefetchMenu(location.id);
        /**
         * Optional: the favourites views render this card without a handler. This
         * previously fell back to `locationsActions.setActiveLocation(location.id)`,
         * which builds an action object rather than dispatching one — React refused
         * to register the non-function value, so nothing happened either way.
         */
        setActiveLocation?.(location.id);
      }}
      /**
       * Start warming as soon as the diner signals intent. react-query de-dupes
       * in-flight fetches and honours `staleTime`, so a location is fetched at most
       * once however many times it is hovered. (TEAM-13663)
       */
      onMouseEnter={() => prefetchMenu(location.id)}
      onFocus={() => prefetchMenu(location.id)}
      $isActive={isActive}
      id={`Location-${location.id}`}
      favorites={favorites}
      editFavoriteLocation={editFavoriteLocation}
      tabIndex={0}
      data-testid="location-card-container"
    >
      <StyledLocationCardFlex>
        {deriveLocationInfo(location)}
        <StyledCardLinks data-css-override="StyledCardLinks">
          {menuCta && <MenuCtaManager label={menuCta} location={location} favorites={favorites} />}

          {/* Show ordering links if menu CTA override doesn't exist */}
          {!menuCta && (
            <>
              {/* Location is active and accepting orders */}
              <LocationCta
                accessor="primary"
                location={location}
                position={LOCATION_ATTRIBUTE_POSITIONS.PRIMARY_ORDER_CTA}
                favorites={favorites}
              />

              <LocationCta
                accessor="secondary"
                location={location}
                position={LOCATION_ATTRIBUTE_POSITIONS.SECONDARY_ORDER_CTA}
                favorites={favorites}
              />
            </>
          )}
        </StyledCardLinks>
      </StyledLocationCardFlex>

      {/* Show footer links if CTA override doesn't exist */}
      {(!menuCta || editFavoriteLocation) && (
        <Stack position="trailing">
          <SecondaryCtaManager location={location} favorites={favorites} />
          {/* For editing Favorite Location */}
          {editFavoriteLocation && <FavoriteLocationActions location={location} />}
        </Stack>
      )}
    </StyledLocationCard>
  );
};
